import { AvisoErrorExportacion } from '@/components/dominio/AvisoErrorExportacion';
import {
  type PropsExcluidas,
  SelectorObrasExcluidas,
} from '@/components/dominio/SelectorObrasExcluidas';
import { TituloOrdenable } from '@/components/dominio/TituloOrdenable';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useViviendaCostos } from '@/hooks/useAvanceMatriz';
import { useExportacion } from '@/hooks/useExportacion';
import { useFiltrosGlobales } from '@/lib/FiltrosGlobalesContext';
import type { FilaTabla, ReporteTablaExport } from '@/lib/exportarViviendaGerencia';
import {
  describirExcluidas,
  describirPeriodo,
  fmtMonto,
  fmtPct,
  razon,
} from '@/lib/formatoGerencia';
import { type OrdenColumna, ordenarHermanos, siguienteOrden } from '@/lib/ordenColumna';
import { CaretDown, CaretRight, CircleNotch, DownloadSimple, FilePdf } from '@phosphor-icons/react';
import { type ReactElement, useMemo, useState } from 'react';

/** Acumulado de una partida (o tarea padre) sobre TODAS las casas filtradas. */
interface Acum {
  costo: number;
  mo: number;
  obras: Set<string>;
}
const nuevo = (): Acum => ({ costo: 0, mo: 0, obras: new Set() });
const matMaq = (a: Acum) => a.costo - a.mo;
/** Costo promedio por obra (solo las obras que registran costo en la partida). */
const promedio = (a: Acum) => (a.obras.size > 0 ? a.costo / a.obras.size : 0);

interface Partida {
  taskNo: string;
  etiqueta: string;
  acum: Acum;
}
interface Tarea {
  padre: string;
  etiqueta: string;
  acum: Acum;
  partidas: Partida[];
}

const natural = (a: string, b: string) => a.localeCompare(b, 'es', { numeric: true });

const COLUMNAS = [
  'Costo total',
  'Materiales y maquinaria',
  'Mano de obra',
  'Mat. y maq. ÷ Costo total',
  'M.O. ÷ Costo total',
  '% del costo de las casas',
  'Obras',
  'Costo promedio por obra',
] as const;

export function ViviendaPartidas({ excluidas, onExcluidas }: PropsExcluidas) {
  const { data, isLoading, error } = useViviendaCostos();
  // Obras que el usuario decidió excluir (se descartan antes de cualquier cálculo).
  const filasObras = useMemo(
    () => (data?.data ?? []).filter((f) => !excluidas.has(f.worksNo)),
    [data, excluidas],
  );
  const { filtros } = useFiltrosGlobales();
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [orden, setOrden] = useState<OrdenColumna | null>(null);
  const { exportando, error: errorExport, ejecutar, limpiarError } = useExportacion();

  const modelo = useMemo(() => {
    const tareas = new Map<string, Tarea>();
    const partidas = new Map<string, Partida>();
    const total = nuevo();
    for (const f of filasObras) {
      let t = tareas.get(f.tareaPadre);
      if (!t) {
        t = {
          padre: f.tareaPadre,
          etiqueta: f.tareaPadreDisplay ?? f.tareaPadre,
          acum: nuevo(),
          partidas: [],
        };
        tareas.set(f.tareaPadre, t);
      }
      let p = partidas.get(f.taskNo);
      if (!p) {
        p = { taskNo: f.taskNo, etiqueta: f.tareaDisplay ?? f.taskNo, acum: nuevo() };
        partidas.set(f.taskNo, p);
        t.partidas.push(p);
      }
      for (const a of [p.acum, t.acum, total]) {
        a.costo += f.costo;
        a.mo += f.costoMO;
        if (f.costo !== 0 || f.costoMO !== 0) a.obras.add(f.worksNo);
      }
    }
    const lista = [...tareas.values()].sort((a, b) => natural(a.padre, b.padre));
    for (const t of lista) t.partidas.sort((a, b) => natural(a.taskNo, b.taskNo));
    return { tareas: lista, total };
  }, [filasObras]);

  const toggle = (key: string) =>
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" /> Cargando…
      </div>
    );
  }
  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error al cargar: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  const { total } = modelo;
  const valores = (a: Acum): (number | null)[] => [
    a.costo || null,
    matMaq(a) || null,
    a.mo || null,
    razon(matMaq(a), a.costo),
    razon(a.mo, a.costo),
    razon(a.costo, total.costo),
    a.obras.size || null,
    promedio(a) || null,
  ];

  const celdas = (a: Acum) => [
    <TableCell key="c" className="text-right text-xs tabular-nums">
      {fmtMonto(a.costo)}
    </TableCell>,
    <TableCell key="m" className="text-right text-xs tabular-nums">
      {fmtMonto(matMaq(a))}
    </TableCell>,
    <TableCell key="o" className="text-right text-xs tabular-nums">
      {fmtMonto(a.mo)}
    </TableCell>,
    <TableCell key="pm" className="bg-emerald-50/60 text-right text-xs font-semibold tabular-nums">
      {fmtPct(matMaq(a), a.costo)}
    </TableCell>,
    <TableCell key="po" className="bg-emerald-50/60 text-right text-xs font-semibold tabular-nums">
      {fmtPct(a.mo, a.costo)}
    </TableCell>,
    <TableCell key="pt" className="bg-emerald-50/60 text-right text-xs font-semibold tabular-nums">
      {fmtPct(a.costo, total.costo)}
    </TableCell>,
    <TableCell key="n" className="text-right text-xs tabular-nums">
      {a.obras.size || '—'}
    </TableCell>,
    <TableCell key="pr" className="text-right text-xs tabular-nums">
      {fmtMonto(promedio(a))}
    </TableCell>,
  ];

  /** Hermanos de un nivel en el orden elegido (o el natural si no hay orden). */
  const hermanos = <T extends { acum: Acum }>(items: T[]) =>
    ordenarHermanos(items, orden, (n) => valores(n.acum));
  const alOrdenar = (idx: number) => setOrden((actual) => siguienteOrden(actual, idx));

  const filas: ReactElement[] = [];
  for (const t of hermanos(modelo.tareas)) {
    const key = `T|${t.padre}`;
    const abierto = expandidos.has(key);
    filas.push(
      <TableRow key={key} className="bg-emerald-100 font-semibold">
        <TableCell className="sticky left-0 z-10 bg-emerald-100 text-xs">
          <button
            type="button"
            onClick={() => toggle(key)}
            className="flex items-center gap-1 hover:underline"
          >
            {abierto ? (
              <CaretDown className="h-3 w-3" weight="bold" />
            ) : (
              <CaretRight className="h-3 w-3" weight="bold" />
            )}
            {t.etiqueta}
          </button>
        </TableCell>
        {celdas(t.acum)}
      </TableRow>,
    );
    if (!abierto) continue;
    for (const p of hermanos(t.partidas)) {
      filas.push(
        <TableRow key={`P|${p.taskNo}`}>
          <TableCell className="sticky left-0 z-10 bg-background text-xs">
            <div className="pl-6">{p.etiqueta}</div>
          </TableCell>
          {celdas(p.acum)}
        </TableRow>,
      );
    }
  }

  // ---- Exportación: la tabla completa (tarea padre + partidas) en Excel y PDF ----
  const armarReporte = (): ReporteTablaExport => {
    const hoy = new Date().toLocaleDateString('es-CR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const filtrosActivos = [
      filtros.filtroProyecto && `Proyecto: ${filtros.filtroProyecto}`,
      filtros.filtroBloque && `Bloque: ${filtros.filtroBloque}`,
      filtros.filtroObra && `Obra: ${filtros.filtroObra}`,
      filtros.filtroModelo && `Modelo: ${filtros.filtroModelo}`,
      filtros.estadosSel?.length && `Estado de venta actual: ${filtros.estadosSel.join(', ')}`,
      describirExcluidas(excluidas, data?.data ?? []),
    ].filter(Boolean);

    const filasExp: FilaTabla[] = [];
    for (const t of hermanos(modelo.tareas)) {
      filasExp.push({ nivel: 0, etiqueta: t.etiqueta, valores: valores(t.acum) });
      for (const p of hermanos(t.partidas)) {
        filasExp.push({ nivel: 1, etiqueta: p.etiqueta, valores: valores(p.acum) });
      }
    }
    filasExp.push({ nivel: 0, total: true, etiqueta: 'Total viviendas', valores: valores(total) });

    return {
      titulo: 'Vivienda Gerencia · Costo por partida',
      subtitulo: [
        'Viviendas VB, VC y VN (área PRO VIVIENDA) · todas las obras seleccionadas, de principio a fin',
        `Período: ${describirPeriodo(filtros)} · Generado el ${hoy}${filtrosActivos.length ? ` · ${filtrosActivos.join(' · ')}` : ''}`,
        'Materiales y maquinaria = costo directo menos mano de obra. Costo promedio por obra: solo obras con costo en la partida.',
      ],
      kpis: [
        { titulo: 'Costo total', valor: fmtMonto(total.costo) },
        { titulo: 'Materiales y maquinaria', valor: fmtMonto(matMaq(total)) },
        { titulo: 'Mano de obra', valor: fmtMonto(total.mo) },
        { titulo: 'Mat. y maq. ÷ Costo', valor: fmtPct(matMaq(total), total.costo) },
        { titulo: 'M.O. ÷ Costo', valor: fmtPct(total.mo, total.costo) },
        { titulo: 'Obras', valor: String(total.obras.size) },
      ],
      tabla: {
        titulo: 'Costo por partida',
        arbol: true,
        encabezado: 'Tarea / Partida',
        columnas: [
          { titulo: COLUMNAS[0], tipo: 'monto' },
          { titulo: COLUMNAS[1], tipo: 'monto' },
          { titulo: COLUMNAS[2], tipo: 'monto' },
          { titulo: COLUMNAS[3], tipo: 'pct' },
          { titulo: COLUMNAS[4], tipo: 'pct' },
          { titulo: COLUMNAS[5], tipo: 'pct' },
          { titulo: COLUMNAS[6], tipo: 'num' },
          { titulo: COLUMNAS[7], tipo: 'monto' },
        ],
        filas: filasExp,
      },
      nombreArchivo: `vivienda-costo-por-partida-${new Date().toISOString().slice(0, 10)}`,
    };
  };

  const exportar = (formato: 'xlsx' | 'pdf') =>
    ejecutar(formato, async () => {
      const rep = armarReporte();
      const lib = await import('@/lib/exportarViviendaGerencia');
      if (formato === 'xlsx') await lib.exportarTablaExcel(rep);
      else await lib.exportarTablaPdf(rep);
    });

  const tarjetas = [
    { titulo: 'Costo total', valor: fmtMonto(total.costo), sub: 'Costo directo de las viviendas' },
    {
      titulo: 'Materiales y maquinaria',
      valor: fmtMonto(matMaq(total)),
      sub: 'Costo total − mano de obra',
    },
    { titulo: 'Mano de obra', valor: fmtMonto(total.mo), sub: 'Cuenta 6010060000000' },
    {
      titulo: 'Mat. y maq. ÷ Costo total',
      valor: fmtPct(matMaq(total), total.costo),
      sub: 'Peso de materiales y maquinaria',
      destacada: true,
    },
    {
      titulo: 'M.O. ÷ Costo total',
      valor: fmtPct(total.mo, total.costo),
      sub: 'Peso de la mano de obra',
      destacada: true,
    },
    { titulo: 'Obras', valor: String(total.obras.size), sub: 'Con costo en el filtro actual' },
  ];

  const botonExport =
    'inline-flex items-center gap-1 rounded-md border border-emerald-700 bg-white px-2 py-1 font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50';
  const todasKeys = new Set(modelo.tareas.map((t) => `T|${t.padre}`));

  return (
    <div className="space-y-4">
      {errorExport && <AvisoErrorExportacion error={errorExport} onCerrar={limpiarError} />}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        {tarjetas.map((c) => (
          <Card key={c.titulo} className={c.destacada ? 'border-emerald-300 bg-emerald-50/60' : ''}>
            <CardContent className="p-4">
              <div className="text-xs font-medium text-muted-foreground">{c.titulo}</div>
              <div className="mt-1 text-xl font-semibold tabular-nums">{c.valor}</div>
              <div className="mt-0.5 text-[11px] text-muted-foreground">{c.sub}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2 text-xs">
            <span className="text-sm font-semibold">Costo por partida (todas las casas)</span>
            <span className="text-zinc-300">|</span>
            <button
              type="button"
              onClick={() => setExpandidos(new Set(todasKeys))}
              className="text-blue-600 hover:underline"
            >
              Expandir todo
            </button>
            <span className="text-zinc-300">|</span>
            <button
              type="button"
              onClick={() => setExpandidos(new Set())}
              className="text-blue-600 hover:underline"
            >
              Colapsar
            </button>
            <span className="text-zinc-300">|</span>
            <SelectorObrasExcluidas
              filas={data?.data ?? []}
              excluidas={excluidas}
              onChange={onExcluidas}
            />
            <span className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => exportar('xlsx')}
                disabled={exportando !== null}
                className={botonExport}
              >
                <DownloadSimple className="h-3.5 w-3.5" weight="bold" />
                {exportando === 'xlsx' ? 'Generando…' : 'Exportar a Excel'}
              </button>
              <button
                type="button"
                onClick={() => exportar('pdf')}
                disabled={exportando !== null}
                className={botonExport}
              >
                <FilePdf className="h-3.5 w-3.5" weight="bold" />
                {exportando === 'pdf' ? 'Generando…' : 'Exportar a PDF'}
              </button>
            </span>
          </div>
          <Table wrapperClassName="max-h-[calc(100vh-300px)]">
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 top-0 z-30 min-w-[260px] bg-background">
                  Tarea / Partida
                </TableHead>
                {COLUMNAS.map((c, i) => (
                  <TableHead
                    key={c}
                    className="sticky top-0 z-20 min-w-[110px] bg-background text-right text-xs"
                  >
                    <TituloOrdenable titulo={c} idx={i} orden={orden} onOrdenar={alOrdenar} />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filas}
              <TableRow className="border-t-2 border-zinc-300 bg-zinc-100 font-bold">
                <TableCell className="sticky left-0 z-10 bg-zinc-100 text-xs">
                  Total viviendas
                </TableCell>
                {celdas(total)}
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <p className="text-[11px] text-muted-foreground">
        Cada partida suma el costo de todas las casas seleccionadas. «Obras» cuenta las casas con
        costo en la partida y el costo promedio las divide entre ellas. «% del costo de las casas»
        es el peso de la partida en el costo total. El filtro de <strong>Estado de venta</strong>{' '}
        aquí excluye obras según su estado <strong>actual</strong> y trae todo su costo de principio
        a fin; los filtros de Proyecto, Bloque, Obra y Modelo también excluyen obras.
      </p>
    </div>
  );
}
